import { useState, useEffect } from 'react';
import { trackPWAEvent } from '@/utils/pwaAnalytics';
import { toast } from '@/hooks/use-toast';

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export const usePWA = () => {
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [isInstalled, setIsInstalled] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Check if app is installed/standalone
    const checkInstallation = () => {
      const standalone = window.matchMedia('(display-mode: standalone)').matches;
      const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
      const isIOSStandalone = iOS && (window.navigator as any).standalone === true;
      
      setIsStandalone(standalone || isIOSStandalone);
      setIsInstalled(standalone || isIOSStandalone);
      
      // iOS Safari doesn't fire beforeinstallprompt, so we manually enable install for iOS
      if (iOS && !isIOSStandalone) {
        // Create a synthetic install prompt for iOS
        setInstallPrompt({} as BeforeInstallPromptEvent);
      }
    };

    checkInstallation();

    // Listen for install prompt
    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      setInstallPrompt(e as BeforeInstallPromptEvent);
    };

    // Listen for app installed
    const handleAppInstalled = () => {
      setIsInstalled(true);
      setInstallPrompt(null);
      trackPWAEvent('install_success', 'badge', false, true);
      toast({
        title: "🎉 WordPop installed!",
        description: "Open from your home screen anytime",
      });
    };

    // Listen for service worker updates
    const handleControllerChange = () => {
      setUpdateAvailable(true);
    };

    // Listen for custom update event from main.tsx
    const handleWordPopUpdate = () => {
      setUpdateAvailable(true);
    };

    // PWA visibility change - check for updates when app regains focus
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && 'serviceWorker' in navigator) {
        navigator.serviceWorker.getRegistration().then(registration => {
          if (registration) {
            registration.update();
          }
        });
      }
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    window.addEventListener('wordpop-update-available', handleWordPopUpdate);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      navigator.serviceWorker.addEventListener('controllerchange', handleControllerChange);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
      window.removeEventListener('wordpop-update-available', handleWordPopUpdate);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.removeEventListener('controllerchange', handleControllerChange);
      }
    };
  }, []);

  const promptInstall = async () => {
    if (!installPrompt) return false;

    try {
      await installPrompt.prompt();
      const { outcome } = await installPrompt.userChoice;
      
      if (outcome === 'accepted') {
        setIsInstalled(true);
        trackPWAEvent('install_success', 'badge', false, true);
        toast({
          title: "🎉 WordPop installed!",
          description: "Open from your home screen anytime",
        });
      } else {
        trackPWAEvent('install_declined', 'badge', true, false);
      }
      
      setInstallPrompt(null);
      return outcome === 'accepted';
    } catch (error) {
      console.error('Install prompt error:', error);
      return false;
    }
  };

  const performUpdate = () => {
    const updateSW = (window as any).__WORDPOP_UPDATE_SW__;
    if (updateSW) {
      updateSW(true);
    } else {
      window.location.reload();
    }
  };

  // iOS Safari users can always "install" (add to home screen)
  const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
  const canInstallOnIOS = iOS && !isInstalled;
  
  return {
    canInstall: (!!installPrompt && !isInstalled) || canInstallOnIOS,
    isInstalled,
    isStandalone,
    updateAvailable,
    promptInstall,
    performUpdate,
  };
};
